import React from 'react';
import { useTranslation } from 'react-i18next';

// Apple-style location pin icon (origin pin)
const PinIcon = () => (
  <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1 .1-1.4z" />
  </svg>
);

const VehicleIcon = () => (
  <svg className="w-7 h-7 text-black" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z" />
  </svg>
);

const NotificationPinIcon = () => (
  <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="currentColor">
    <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1 .1-1.4z" />
  </svg>
);

const AppStoreIcon = () => (
  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const GooglePlayIcon = () => (
  <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.609 1.814L13.792 12 3.609 22.186a.996.996 0 01-.609-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.807 1.626a1 1 0 010 1.732l-2.808 1.626L15.119 12l2.579-2.492zM5.864 2.658L16.802 8.99l-2.302 2.302-8.636-8.634z" />
  </svg>
);

export function TrackRouteSection() {
  const { t } = useTranslation();

  return (
    <section className="py-24 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="border border-gray-200 rounded-3xl overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0 items-stretch">
            {/* Left: text + buttons */}
            <div className="p-10 md:p-16 flex flex-col justify-center">
              <h2 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight mb-6">{t('landing.trackRoute.title')}</h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-10 max-w-md">{t('landing.trackRoute.subtitle')}</p>
              <div className="flex flex-wrap gap-3">
                <a href="#" className="inline-block">
                  <div className="bg-black text-white rounded-lg px-5 py-3 flex items-center gap-3 hover:opacity-90 transition">
                    <AppStoreIcon />
                    <div className="text-left leading-tight">
                      <div className="text-[10px] uppercase tracking-wide">{t('landing.trackRoute.appStoreSmall')}</div>
                      <div className="text-base font-semibold">{t('landing.trackRoute.appStoreBig')}</div>
                    </div>
                  </div>
                </a>
                <a href="#" className="inline-block">
                  <div className="bg-black text-white rounded-lg px-5 py-3 flex items-center gap-3 hover:opacity-90 transition">
                    <GooglePlayIcon />
                    <div className="text-left leading-tight">
                      <div className="text-[10px] uppercase tracking-wide">{t('landing.trackRoute.googlePlaySmall')}</div>
                      <div className="text-base font-semibold">{t('landing.trackRoute.googlePlayBig')}</div>
                    </div>
                  </div>
                </a>
              </div>
            </div>

            {/* Right: map illustration */}
            <div className="relative bg-gray-50 min-h-[400px] overflow-hidden">
              <svg viewBox="0 0 600 500" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
                <rect width="600" height="500" fill="#f3f4f6" />
                {/* Streets — horizontal */}
                <g stroke="#e5e7eb" strokeWidth="2" fill="none">
                  <path d="M0 80 L600 60" />
                  <path d="M0 160 L600 140" />
                  <path d="M0 240 L600 220" />
                  <path d="M0 320 L600 300" />
                  <path d="M0 400 L600 380" />
                  <path d="M0 470 L600 450" />
                  <path d="M80 0 L100 500" />
                  <path d="M180 0 L200 500" />
                  <path d="M280 0 L300 500" />
                  <path d="M380 0 L400 500" />
                  <path d="M480 0 L500 500" />
                </g>
                <g stroke="#d1d5db" strokeWidth="3" fill="none">
                  <path d="M0 200 Q300 180 600 240" />
                  <path d="M150 0 Q170 250 200 500" />
                  <path d="M420 0 Q440 250 460 500" />
                </g>
                {/* Building blocks */}
                <g fill="#e5e7eb" opacity="0.6">
                  <rect x="110" y="90" width="60" height="60" />
                  <rect x="210" y="90" width="60" height="60" />
                  <rect x="310" y="80" width="60" height="60" />
                  <rect x="410" y="80" width="60" height="60" />
                  <rect x="110" y="170" width="60" height="60" />
                  <rect x="310" y="160" width="60" height="60" />
                  <rect x="410" y="160" width="60" height="60" />
                  <rect x="110" y="250" width="60" height="60" />
                  <rect x="210" y="240" width="60" height="60" />
                  <rect x="310" y="240" width="60" height="60" />
                  <rect x="110" y="330" width="60" height="60" />
                  <rect x="210" y="320" width="60" height="60" />
                  <rect x="410" y="320" width="60" height="60" />
                  <rect x="310" y="410" width="60" height="60" />
                  <rect x="410" y="400" width="60" height="60" />
                </g>
                {/* Route line */}
                <path
                  d="M 230 130 L 230 210 L 360 210 L 360 340 L 510 340"
                  stroke="#1f1f1f"
                  strokeWidth="5"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              {/* Origin pin (prestador) */}
              <div className="absolute" style={{ left: '34%', top: '20%' }}>
                <div className="relative">
                  <div className="bg-black w-12 h-12 rounded-full flex items-center justify-center shadow-lg">
                    <PinIcon />
                  </div>
                  <div className="absolute left-1/2 -translate-x-1/2 -bottom-2 w-3 h-3 bg-black rotate-45" />
                </div>
              </div>

              {/* Vehicle on route */}
              <div className="absolute" style={{ left: '58%', top: '63%' }}>
                <div className="bg-white w-12 h-12 rounded-full flex items-center justify-center shadow-lg border border-gray-200">
                  <VehicleIcon />
                </div>
              </div>

              {/* Destination (cliente) */}
              <div className="absolute" style={{ left: '83%', top: '63%' }}>
                <div className="relative">
                  <div className="w-12 h-12 rounded-full bg-blue-200 opacity-50 absolute -inset-2 animate-pulse" />
                  <div className="bg-blue-500 w-6 h-6 rounded-full border-4 border-white shadow-lg relative" />
                </div>
              </div>

              {/* Notification card */}
              <div className="absolute top-4 right-4 bg-white rounded-xl shadow-xl p-4 max-w-[260px] border border-gray-100">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-6 h-6 bg-black rounded-md flex items-center justify-center">
                    <NotificationPinIcon />
                  </div>
                  <span className="text-xs font-bold tracking-wider text-gray-500">{t('landing.trackRoute.notificationBrand')}</span>
                  <span className="text-xs text-gray-400 ml-auto">{t('landing.trackRoute.notificationTime')}</span>
                </div>
                <div className="text-sm font-semibold text-gray-900">{t('landing.trackRoute.notificationTitle')}</div>
                <div className="text-xs text-gray-600">{t('landing.trackRoute.notificationBody')}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
